import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthShell from "@/pages/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Check, Circle, Globe } from "lucide-react";
import { setAppLanguage, SUPPORTED_LANGUAGES } from "@/i18n";
import { LockNotice } from "@/components/LockNotice";
import { COUNTRIES, detectCountryCode } from "@/lib/countries";

export default function Signup() {
  const [form, setForm] = useState({ full_name: "", email: "", password: "", account_type: "individual", org_name: "", country: detectCountryCode() });
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [lockNotice, setLockNotice] = useState("");
  const { setSession } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const legalPfx = (i18n.language || "en").split("-")[0] === "fr" ? "/fr" : "";

  const pwRules = [
    { label: t("auth.pwMin"), ok: form.password.length >= 8 },
    { label: t("auth.pwLetter"), ok: /[a-zA-Z]/.test(form.password) },
    { label: t("auth.pwNumber"), ok: /[0-9]/.test(form.password) },
  ];
  const pwMatches = confirm.length > 0 && form.password === confirm;
  const pwValid = pwRules.every((r) => r.ok) && pwMatches;

  const submit = async (e) => {
    e.preventDefault();
    if (!pwValid || !agreed) return;
    setLoading(true);
    setLockNotice("");
    try {
      const lang = (i18n.language || "en").split("-")[0];
      const res = await api.post("/auth/signup", { ...form, language: lang });
      if (res.data.token) {
        setSession(res.data.token, res.data.user);
        toast.success(t("auth.welcomeTrial"));
        navigate("/app/log");
      } else {
        toast.success(res.data.message || t("auth.checkEmail"), { duration: 8000 });
        navigate("/login");
      }
    } catch (err) {
      if (err?.response?.status === 429) {
        setLockNotice(apiError(err, t("auth.lockedFallback")));
      } else {
        toast.error(apiError(err, t("common.somethingWrong")));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={t("auth.trialTitle")} subtitle={t("auth.trialSubtitle")}>
      {lockNotice && (
        <LockNotice message={lockNotice} ctaTo="/login" ctaLabel={t("auth.loginToContinue")} />
      )}
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <div data-testid="signup-language-row">
          <Label className="flex items-center gap-1.5"><Globe className="h-3.5 w-3.5 text-[#202123]" /> {t("language.label")}</Label>
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            {SUPPORTED_LANGUAGES.map((l) => {
              const active = (i18n.language || "en").split("-")[0] === l.code;
              return (
                <button type="button" key={l.code} onClick={() => setAppLanguage(l.code)}
                  data-testid={`signup-lang-${l.code}`}
                  className={`rounded-md border px-3 py-2.5 text-sm font-medium transition-colors ${
                    active ? "border-[#C5C5D0] bg-accent text-accent-foreground" : "border-slate-300 text-slatebody hover:border-slate-400"
                  }`}>
                  {l.native}
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-2" data-testid="account-type-choice">
          {["individual", "organization"].map((tp) => (
            <button type="button" key={tp} onClick={() => setForm({ ...form, account_type: tp })}
              data-testid={`account-type-${tp}`}
              className={`text-left rounded-md border px-3.5 py-3 transition-colors ${
                form.account_type === tp ? "border-[#C5C5D0] bg-accent" : "border-slate-300 hover:border-slate-400"
              }`}>
              <span className={`block text-sm font-medium ${form.account_type === tp ? "text-accent-foreground" : "text-navy"}`}>
                {tp === "organization" ? t("auth.acctOrgLabel") : t("auth.acctIndividualLabel")}
              </span>
              <span className="block text-xs text-slatebody mt-0.5">
                {tp === "organization" ? t("auth.acctOrgHelp") : t("auth.acctIndividualHelp")}
              </span>
            </button>
          ))}
        </div>
        {form.account_type === "organization" && (
          <div className="rounded-md border border-[#C5C5D0] bg-accent/40 px-4 py-3" data-testid="signup-org-message">
            <p className="text-sm text-navy leading-relaxed">{t("auth.orgSignupMsg")}</p>
            <p className="text-xs text-slatebody mt-1.5 leading-relaxed">{t("auth.orgSignupSupport")}</p>
          </div>
        )}
        {form.account_type === "individual" && (
          <div className="rounded-md border border-[#C5C5D0] bg-accent/40 px-4 py-3" data-testid="signup-individual-message">
            <p className="text-sm text-navy leading-relaxed">{t("auth.indivSignupMsg")}</p>
          </div>
        )}
        <div>
          <Label htmlFor="name">{t("auth.fullName")}</Label>
          <Input id="name" required value={form.full_name} onChange={set("full_name")} className="mt-1.5" data-testid="signup-name" />
        </div>
        {form.account_type === "organization" && (
          <div>
            <Label htmlFor="org">{t("auth.orgName")}</Label>
            <Input id="org" required value={form.org_name} onChange={set("org_name")} className="mt-1.5" data-testid="signup-org" />
            <p className="text-xs text-slatebody mt-1.5" data-testid="signup-org-hint">{t("auth.orgHint")}</p>
          </div>
        )}
        <div>
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input id="email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5" data-testid="signup-email" />
        </div>
        <div>
          <Label htmlFor="country">{t("auth.country")}</Label>
          <select id="country" value={form.country} onChange={set("country")} data-testid="signup-country"
            className="mt-1.5 w-full h-10 rounded-md border border-[#C5C5D0] bg-white px-3 text-sm">
            <option value="">{t("auth.countrySelect")}</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            <option value="XX">{t("auth.countryOther")}</option>
          </select>
          <p className="text-xs text-slatebody mt-1.5" data-testid="signup-country-hint">{t("auth.countryHint")}</p>
        </div>
        <div>
          <Label htmlFor="password">{t("auth.password")}</Label>
          <PasswordInput id="password" required value={form.password} onChange={set("password")} className="mt-1.5" data-testid="signup-password" placeholder={t("auth.pwPlaceholder")} />
          <ul className="mt-3 space-y-1.5" data-testid="signup-password-rules">
            {pwRules.map((r, i) => (
              <li key={i} className={`flex items-center gap-2 text-xs transition-colors ${r.ok ? "text-[#202123]" : "text-slatebody"}`} data-testid={`signup-password-rule-${i}${r.ok ? "-ok" : ""}`}>
                {r.ok ? <Check className="h-3.5 w-3.5" /> : <Circle className="h-3 w-3" />}
                {r.label}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <Label htmlFor="confirm">{t("auth.confirmPassword")}</Label>
          <PasswordInput id="confirm" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" data-testid="signup-confirm" placeholder={t("auth.reenter")} />
          {confirm.length > 0 && !pwMatches && <p className="text-xs text-red-600 mt-1.5" data-testid="signup-mismatch">{t("auth.mismatch")}</p>}
        </div>
        <label className="flex items-start gap-2.5 cursor-pointer" data-testid="signup-consent-row">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-[#202123] shrink-0" data-testid="signup-consent-checkbox" />
          <span className="text-sm text-slatebody leading-snug">
            {t("auth.consentPre")}{" "}
            <Link to={`${legalPfx}/terms`} target="_blank" rel="noopener noreferrer" className="text-[#202123] font-medium hover:underline" data-testid="signup-terms-link">{t("auth.tosFull")}</Link>,{" "}
            <Link to={`${legalPfx}/privacy`} target="_blank" rel="noopener noreferrer" className="text-[#202123] font-medium hover:underline" data-testid="signup-privacy-link">{t("auth.privacyFull")}</Link>,{" "}
            <Link to={`${legalPfx}/ai-use`} target="_blank" rel="noopener noreferrer" className="text-[#202123] font-medium hover:underline" data-testid="signup-aiuse-link">{t("auth.aiUseFull")}</Link>,{" "}{t("auth.consentAnd")}{" "}
            <Link to={`${legalPfx}/cookies`} target="_blank" rel="noopener noreferrer" className="text-[#202123] font-medium hover:underline" data-testid="signup-cookies-link">{t("auth.cookiesFull")}</Link>.
          </span>
        </label>
        <Button type="submit" disabled={loading || !pwValid || !agreed} className="w-full bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" data-testid="signup-submit">
          {loading ? t("auth.creating") : t("auth.createAccountBtn")}
        </Button>
      </form>
      <p className="text-sm text-slatebody mt-6 text-center">
        {t("auth.haveAccount")} <Link to="/login" className="text-[#202123] font-medium hover:underline" data-testid="to-login">{t("auth.loginToContinue")}</Link>
      </p>
    </AuthShell>
  );
}
